import s from "./Player.module.scss";
import { FC, useEffect, useRef } from "react";
import { PlayCanvasIntegration } from "./components/PlayCanvasIntegration/PlayCanvasIntegration";
import { PlayerSettings } from "../PlayerSettings/PlayerSettings";
import { Resolution } from "../Resolution/Resolution";
import { Toolbar } from "../Toolbar/Toolbar";
import { RemoveObject } from "../RemoveObject/RemoveObject";
import { CameraPreset } from "../../hooks/useCameraPresets";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getSceneHintState } from "../../redux/reducers/app/appSelectors";
import { SceneHint } from "../../shared/SceneHint/SceneHint";
import { usePlayCanvasProgress } from "../../hooks/usePlayCanvasProgress";
import Preloader from "../../shared/Preloader/Preloader";
import { InfoMessageInPlayer } from "../InfoMessageInPlayer/InfoMessageInPlayer";
import {
  disableSceneHint,
  setShowOverlayHint,
  toggleSceneHint,
} from "../../redux/reducers/app/appReducer";
import SceneHintOverlay from "../../shared/SceneHintOverlay/SceneHintOverlay";

interface PlayerProps {
  presets: CameraPreset[];
  activePreset: CameraPreset | null;
  setActivePreset: (preset: CameraPreset) => void;
  deletePreset: (presetId: string) => Promise<void>;
  savePreset: (customName?: string, forceLocal?: boolean) => Promise<void>;
  loadPresets: () => Promise<void>;
}

const Player: FC<PlayerProps> = ({
  presets,
  activePreset,
  setActivePreset,
  deletePreset,
  savePreset,
  loadPresets,
}) => {
  const sceneHintState = useAppSelector(getSceneHintState());
  const { isReady } = usePlayCanvasProgress();
  const dispatch = useAppDispatch();
  const hasDisabledSceneHintRef = useRef(false);

  const isShowOverlayHint = useAppSelector(
    state => state.app.isShowOverlayHint
  );

  // Open Overlay hints after clikcin on the H button.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;

      const isTypingElement =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);

      if (event.repeat || isTypingElement) return;

      if (
        event.key?.toLowerCase() === "h" ||
        event.key?.toLowerCase() === "р"
      ) {
        event.preventDefault();
        dispatch(setShowOverlayHint(true));
        hasDisabledSceneHintRef.current = false;
      } else if (event.key?.toLowerCase() === "escape") {
        dispatch(setShowOverlayHint(false));

        if (!hasDisabledSceneHintRef.current) {
          dispatch(disableSceneHint({ isShow: true }));
          hasDisabledSceneHintRef.current = true;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [dispatch]);

  return (
    <>
      <div className={s.wrapper} id="playerContainer">
        <div className={s.canvasArea}>
          <PlayCanvasIntegration />

          {/* Лоадер с прогресс-баром */}
          {!isReady && (
            <div className={s.loaderOverlay}>
              <div className={s.loaderContent}>
                <Preloader />
              </div>
            </div>
          )}
        </div>
        <div className={s.interfaceSidebar}>
          {/* <CloseFullWindow /> */}
          <Resolution />
          <Toolbar
            presets={presets}
            activePreset={activePreset}
            setActivePreset={setActivePreset}
            deletePreset={deletePreset}
            savePreset={savePreset}
            loadPresets={loadPresets}
          />
          <InfoMessageInPlayer />

          <RemoveObject />
          <PlayerSettings />
          {sceneHintState.isShow && <SceneHint />}
        </div>
      </div>

      {isShowOverlayHint && <SceneHintOverlay />}
    </>
  );
};

export default Player;
